import safeStorage from '@github-ui/safe-storage'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {sanitize} from '@github-ui/sanitize'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {DiffLineType} from '@github-ui/diffs/types'
import type {SuggestedChangeLineRange} from '@github-ui/diff-lines/conversation-types'
import type {UnifiedBatchItem, UnifiedBatchItemKind} from '../stores/slices/unified-batch'
import {isSafeSourceUri, safeHttpUrl} from './unified-batch-url-safety'

const UNIFIED_BATCH_STORAGE_SUFFIX = '-active-unified-batch'

/**
 * Gets the sessionStorage key for the unified batch.
 *
 * Distinct suffix from the legacy `suggestion-batch-storage` helper so legacy and unified
 * payloads cannot cross-deserialize when both code paths run side-by-side behind a flag.
 */
export function getUnifiedBatchStorageKey(pullRequestPath: string, headOid: string): string {
  return `${pullRequestPath}-${headOid}${UNIFIED_BATCH_STORAGE_SUFFIX}`
}

/** Current storage payload schema version. Bump on any breaking change to {@link UnifiedBatchItem}. */
const UNIFIED_BATCH_STORAGE_VERSION = 2

type UnifiedBatchStoragePayload = {
  version: typeof UNIFIED_BATCH_STORAGE_VERSION
  items: UnifiedBatchItem[]
  savedAt: number
}

const VALID_KINDS: ReadonlySet<UnifiedBatchItemKind> = new Set([
  'ccr-with-suggestion',
  'ccr-without-suggestion',
  'human-with-suggestion',
  'human-without-suggestion',
])

/** Shape check for a {@link SuggestedChangeLineRange} parsed from storage. */
function isValidLineRange(value: unknown): value is SuggestedChangeLineRange {
  if (!value || typeof value !== 'object') return false
  const r = value as Record<string, unknown>
  return (
    typeof r.startLineNumber === 'number' &&
    typeof r.endLineNumber === 'number' &&
    typeof r.startOrientation === 'string' &&
    typeof r.endOrientation === 'string'
  )
}

/** Shape check for the snapshot `author` parsed from storage. */
function isValidSnapshotAuthor(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const a = value as Record<string, unknown>
  return (
    typeof a.login === 'string' &&
    (a.avatarUrl === undefined || typeof a.avatarUrl === 'string') &&
    (a.url === undefined || typeof a.url === 'string') &&
    (a.isCopilot === undefined || typeof a.isCopilot === 'boolean')
  )
}

const VALID_DIFF_LINE_TYPES: ReadonlySet<string> = new Set(Object.values(DiffLineType))

/**
 * Shape check for a single `StaticDiffLine` (minus `__id`) parsed from storage.
 * The panel's `AutomatedReviewDiffEntry`/`StaticUnifiedDiffPreview` consumer
 * dereferences every field, so a malformed line (e.g. `null`, or missing
 * `html`/`type`) must be rejected rather than branded as valid.
 */
function isValidDiffLine(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const l = value as Record<string, unknown>
  return (
    (l.left === null || typeof l.left === 'number') &&
    (l.right === null || typeof l.right === 'number') &&
    typeof l.html === 'string' &&
    typeof l.text === 'string' &&
    typeof l.type === 'string' &&
    VALID_DIFF_LINE_TYPES.has(l.type)
  )
}

/**
 * Shape check for `ccr-with-suggestion` `suggestionFiles` (an array of
 * {@link DiffEntry}-shaped objects) parsed from storage. Validates the `path`
 * plus every diff line so the panel renderer can't be handed a malformed entry.
 */
function isValidSuggestionFiles(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(entry => {
      if (!entry || typeof entry !== 'object') return false
      const e = entry as Record<string, unknown>
      return typeof e.path === 'string' && Array.isArray(e.diffLines) && e.diffLines.every(isValidDiffLine)
    })
  )
}

/**
 * Lightweight validation that an entry parsed from storage carries every
 * discriminator the slice depends on AND that the `kind`/`hasSuggestion`/
 * `suggestionIndex`/`lineRange` combination agrees with the discriminated-union
 * invariants. Anything that fails here is dropped rather than seeding the slice
 * with a malformed item and crashing a selector downstream.
 */
function isValidStoredItem(value: unknown): value is UnifiedBatchItem {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>

  const hasValidBase =
    typeof v.commentDatabaseId === 'number' &&
    typeof v.commentGlobalId === 'string' &&
    typeof v.path === 'string' &&
    typeof v.hasSuggestion === 'boolean' &&
    typeof v.isOutdated === 'boolean' &&
    typeof v.kind === 'string' &&
    VALID_KINDS.has(v.kind as UnifiedBatchItemKind) &&
    (v.threadId === undefined || v.threadId === null || typeof v.threadId === 'number') &&
    (v.sourceUri === undefined || typeof v.sourceUri === 'string') &&
    // Comment snapshot fields captured at add-to-batch time (storage v2).
    isValidSnapshotAuthor(v.author) &&
    typeof v.createdAt === 'string' &&
    typeof v.bodyHTML === 'string' &&
    typeof v.bodyTruncated === 'boolean' &&
    (v.pathLineRanges === undefined ||
      (Array.isArray(v.pathLineRanges) &&
        v.pathLineRanges.every(range => {
          if (!range || typeof range !== 'object') return false
          const r = range as Record<string, unknown>
          return typeof r.path === 'string' && isValidLineRange(r.lineRange)
        })))
  if (!hasValidBase) return false

  switch (v.kind as UnifiedBatchItemKind) {
    case 'ccr-with-suggestion':
      return (
        v.hasSuggestion === true &&
        v.suggestionIndex === undefined &&
        (v.lineRange === undefined || isValidLineRange(v.lineRange)) &&
        isValidSuggestionFiles(v.suggestionFiles)
      )
    case 'ccr-without-suggestion':
      return (
        v.hasSuggestion === false &&
        v.suggestionIndex === undefined &&
        (v.lineRange === undefined || isValidLineRange(v.lineRange))
      )
    case 'human-with-suggestion':
      return (
        v.hasSuggestion === true &&
        typeof v.suggestionIndex === 'number' &&
        isValidLineRange(v.lineRange) &&
        typeof v.suggestionBlobHTML === 'string'
      )
    case 'human-without-suggestion':
      return v.hasSuggestion === false && v.suggestionIndex === undefined && v.lineRange === undefined
    default:
      return false
  }
}

// Configure our own storage instead of `jsonSafeStorage` so quota errors on set
// are re-thrown (`jsonSafeStorage` hardcodes `throwQuotaErrorsOnSet: false` and
// swallows them). We need to observe a failed write to fall back to a smaller
// payload rather than silently dropping the whole batch.
const storage = safeStorage<Partial<UnifiedBatchStoragePayload>>(
  'sessionStorage',
  {throwQuotaErrorsOnSet: true},
  ssrSafeWindow,
  JSON.parse,
  JSON.stringify,
)

/**
 * Drops the display-only suggestion previews (CCR's `suggestionFiles` and the human
 * `suggestionBlobHTML`) so an oversized batch still persists. The item stays
 * committable; it just renders without its inline preview after a reload.
 */
function stripPreviewFields(items: UnifiedBatchItem[]): UnifiedBatchItem[] {
  return items.map(item => {
    if (item.kind === 'ccr-with-suggestion') return {...item, suggestionFiles: []}
    if (item.kind === 'human-with-suggestion') return {...item, suggestionBlobHTML: sanitizeHtml('')}
    return item
  })
}

function getUnifiedBatchStorageKeysForPullRequest(pullRequestPath: string): string[] {
  const prefix = `${pullRequestPath}-`
  return storage.getKeys().filter(key => key.startsWith(prefix) && key.endsWith(UNIFIED_BATCH_STORAGE_SUFFIX))
}

type StoredUnifiedBatch = {
  items: UnifiedBatchItem[]
  savedAt: number
}

function loadUnifiedBatchFromKey(key: string): StoredUnifiedBatch | undefined {
  const payload = storage.getItem(key)
  if (!payload || typeof payload !== 'object') return undefined
  if (payload.version !== UNIFIED_BATCH_STORAGE_VERSION) return undefined
  if (!Array.isArray(payload.items)) return undefined
  return {
    items: payload.items.filter(isValidStoredItem).map(sanitizeStoredItem),
    // Payloads written before savedAt was added remain recoverable. They sort
    // behind timestamped payloads when more than one old-head batch exists.
    savedAt: typeof payload.savedAt === 'number' ? payload.savedAt : 0,
  }
}

function markRecoveredItemsOutdated(items: UnifiedBatchItem[]): UnifiedBatchItem[] {
  return items.map(item => (item.isOutdated ? item : {...item, isOutdated: true}))
}

export function saveUnifiedBatchToStorage(pullRequestPath: string, headOid: string, items: UnifiedBatchItem[]): void {
  const key = getUnifiedBatchStorageKey(pullRequestPath, headOid)
  try {
    storage.setItem(key, {version: UNIFIED_BATCH_STORAGE_VERSION, items, savedAt: Date.now()})
  } catch {
    // sessionStorage quota is shared across the origin, so a write can fail even
    // for a modest batch once other features have filled it. Retry without the
    // display-only suggestion previews so the committable batch still persists.
    try {
      storage.setItem(key, {
        version: UNIFIED_BATCH_STORAGE_VERSION,
        items: stripPreviewFields(items),
        savedAt: Date.now(),
      })
    } catch {
      // Avoid reloading a stale batch when neither representation can be persisted.
      storage.removeItem(key)
    }
  }
}

export function loadUnifiedBatchFromStorage(pullRequestPath: string, headOid: string): UnifiedBatchItem[] {
  const currentHeadKey = getUnifiedBatchStorageKey(pullRequestPath, headOid)
  const currentHeadBatch = loadUnifiedBatchFromKey(currentHeadKey)
  if (currentHeadBatch && currentHeadBatch.items.length > 0) return currentHeadBatch.items

  const recoveredBatch = getUnifiedBatchStorageKeysForPullRequest(pullRequestPath)
    .filter(key => key !== currentHeadKey)
    .map(key => ({key, batch: loadUnifiedBatchFromKey(key)}))
    .filter(
      (candidate): candidate is {key: string; batch: StoredUnifiedBatch} =>
        candidate.batch !== undefined && candidate.batch.items.length > 0,
    )
    .sort((a, b) => b.batch.savedAt - a.batch.savedAt || b.key.localeCompare(a.key))[0]?.batch

  return recoveredBatch ? markRecoveredItemsOutdated(recoveredBatch.items) : []
}

/**
 * Re-sanitizes an item's HTML fields (`bodyHTML`, `suggestionBlobHTML`, each
 * diff line's `html`) after hydration. They were branded `SafeHTMLString` from a
 * server-sanitized source, but sessionStorage is writable by any same-origin
 * script, so we can't trust the parsed strings across the storage boundary. The
 * panel's VerifiedHTML sinks do no runtime sanitization, so we do it here.
 */
function sanitizeStoredItem(item: UnifiedBatchItem): UnifiedBatchItem {
  const bodyHTML = sanitizeHtml(item.bodyHTML)
  // `sourceUri` crosses the untrusted sessionStorage boundary and later flows into
  // `window.location.href` for outdated rows, so drop anything that isn't a
  // same-origin http(s) URL (e.g. a planted `javascript:` or cross-origin URL, or a
  // value that would throw from `new URL(...)` during render).
  const sourceUri = isSafeSourceUri(item.sourceUri) ? item.sourceUri : undefined
  // `author.avatarUrl`/`author.url` are likewise untrusted: `avatarUrl` flows into
  // `GitHubAvatar`'s uncaught `new URL(src)` (a malformed value would throw and blank
  // the whole panel, which sits under `ErrorBoundary fallback={null}`), and `url` flows
  // into the author link's `href`. Drop anything that isn't an http(s) URL.
  const author = {
    ...item.author,
    avatarUrl: safeHttpUrl(item.author.avatarUrl),
    url: safeHttpUrl(item.author.url),
  }
  switch (item.kind) {
    case 'ccr-with-suggestion':
      return {
        ...item,
        bodyHTML,
        sourceUri,
        author,
        suggestionFiles: item.suggestionFiles.map(entry => ({
          ...entry,
          diffLines: entry.diffLines.map(line => ({...line, html: sanitizeHtml(line.html)})),
        })),
      }
    case 'human-with-suggestion':
      return {...item, bodyHTML, sourceUri, author, suggestionBlobHTML: sanitizeHtml(item.suggestionBlobHTML)}
    default:
      return {...item, bodyHTML, sourceUri, author}
  }
}

function sanitizeHtml(value: string): SafeHTMLString {
  return sanitize(value, {RETURN_TRUSTED_TYPE: false})
}

export function clearUnifiedBatchFromStorage(pullRequestPath: string, headOid: string): void {
  const key = getUnifiedBatchStorageKey(pullRequestPath, headOid)
  storage.removeItem(key)
  for (const oldKey of getUnifiedBatchStorageKeysForPullRequest(pullRequestPath)) {
    storage.removeItem(oldKey)
  }
}
